import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { POPOVER_Z_INDEX } from '@/components/ds2/z-index'

/**
 * The filter-bar portaled-popover mechanics, extracted so every picker shares ONE implementation
 * (BoxTruckEquipmentSelect, SearchForDriverSelect): anchor-rect capture on open, outside-click and
 * scroll close, and measure-before-paint positioning that flips above the trigger when there's no
 * room below. Panel size changes (filtering shrinks the list) reposition via a ResizeObserver, so
 * callers don't thread their content state into the positioning effect.
 *
 * The caller renders its own trigger + portaled panel and spreads `panelPositionStyle` into the
 * panel's `style` (adding its own minWidth/maxHeight).
 */
export const useAnchoredPopover = ({ repositionOnResize = true, panelWidth }: { repositionOnResize?: boolean; panelWidth?: number } = {}) => {
    const [open, setOpen] = useState(false)
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    const openPanel = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchorRect(rect)
        setPos(null)
        setOpen(true)
    }, [])

    const close = useCallback(() => setOpen(false), [])

    /** Trigger onClick: close when open, else capture the anchor and open. */
    const togglePanel = useCallback(() => {
        if (open) setOpen(false)
        else openPanel()
    }, [open, openPanel])

    // Close on outside click (panel is portaled, so check both refs) and on scroll (position is fixed once).
    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node
            if (containerRef.current?.contains(t) || panelRef.current?.contains(t)) return
            // A dropdown opened FROM inside this panel renders its list in its own portal, so the list is not
            // a DOM descendant of the panel and a click on an option looked like an outside click: the panel
            // closed, the option unmounted, and the value never changed. That is what made Load Type, Max Age
            // and Contact impossible to set inside Advanced Filters. An open listbox is never "outside" —
            // the user reached it through a trigger that IS inside.
            if (t instanceof Element && t.closest('[role="listbox"]')) return
            setOpen(false)
        }
        const onScroll = (e: Event) => {
            const t = e.target as Node
            if (panelRef.current?.contains(t)) return
            // Same reasoning as above: scrolling a long option list must not close the panel behind it.
            if (t instanceof Element && t.closest('[role="listbox"]')) return
            setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        window.addEventListener('scroll', onScroll, true)
        return () => {
            document.removeEventListener('mousedown', onDown)
            window.removeEventListener('scroll', onScroll, true)
        }
    }, [open])

    // Measure before paint: flip above the trigger when there's no room below, align to the trigger's
    // width. Re-measures whenever the panel resizes (its content changed), not just on open.
    useLayoutEffect(() => {
        if (!open || !anchorRect) return
        const el = panelRef.current
        if (!el) return
        const measure = () => {
            const h = el.offsetHeight
            const spaceBelow = window.innerHeight - anchorRect.bottom
            const top =
                spaceBelow >= h + 8 || spaceBelow >= anchorRect.top
                    ? Math.min(anchorRect.bottom + 8, window.innerHeight - h - 8)
                    : Math.max(8, anchorRect.top - h - 8)
            setPos({ top, left: anchorRect.left, width: anchorRect.width })
        }
        measure()
        if (!repositionOnResize) return
        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => observer.disconnect()
    }, [open, anchorRect, repositionOnResize])

    // Hidden until the first measurement lands so the panel never paints at the unflipped position.
    const anchoredLeft = pos ? pos.left : (anchorRect?.left ?? 0)
    let width = pos ? pos.width : anchorRect?.width
    let left = anchoredLeft
    if (panelWidth != null) {
        width = Math.min(panelWidth, window.innerWidth - 16)
        left = Math.max(8, Math.min(anchoredLeft, window.innerWidth - width - 8))
    }
    const panelPositionStyle: CSSProperties = {
        position: 'fixed',
        top: pos ? pos.top : (anchorRect?.bottom ?? 0) + 8,
        left,
        width,
        zIndex: POPOVER_Z_INDEX,
        visibility: pos ? 'visible' : 'hidden',
    }

    return { open, anchorRect, containerRef, triggerRef, panelRef, openPanel, close, togglePanel, panelPositionStyle }
}
